import type { DocNode } from '@atlaskit/adf-schema/doc';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import type { AbortOptions, AbortReason } from '../types/abort-reason';
import { log } from '../utils/aifc-logging';
import { isStrictFifoToolCallsEnabled } from '../utils/strict-fifo-tool-calls';
import { createWarningStreamPayload } from './analytics';
import type { TimeoutWarningPayload } from './analytics';
import type {
	IStreamingCommandHandler,
	PassThroughCommandProps,
	StreamCommandProperties,
	StreamType,
} from './command-handler';
import { CommandHandler } from './command-handler';

/**
 * How long a stream may stall before it is aborted (see
 * `startInactivityTimeout`).
 */
const ABORT_TIMEOUT_MS = 30_000;
/**
 * Stalls longer than this are reported as `delayed` once the stream resumes.
 * Reporting only — it never aborts a stream.
 */
const DELAYED_REPORTING_THRESHOLD_MS = 10_000;
/**
 * How long a stream may stall before the `warning` instrumentation fires.
 * Reporting only — it never aborts a stream.
 */
const WARNING_TIMEOUT_MS = 8_000;

type StrictAction = {
	aborted: boolean;
	closed: boolean;
	completion?: Promise<void>;
	started: boolean;
	work: Promise<void>;
};

type ActionPolicy = {
	aborted: boolean;
	closed: boolean;
	id: string;
	ready?: Promise<void>;
	strictFifo: boolean;
	work?: Promise<void>;
};

/**
 * Abstract base class for command handlers that support streaming operations.
 * Manages the lifecycle of streaming commands including timeout handling,
 * state management, and coordination with the editor API.
 *
 * @template TCommandProperties - The specific command properties type for this handler,
 *                                must extend StreamCommandProperties
 */
export abstract class StreamingCommandHandler<TCommandProperties extends StreamCommandProperties>
	extends CommandHandler<TCommandProperties, void>
	implements IStreamingCommandHandler
{
	/** Flag tracking whether a streaming operation is currently active */
	private isStreaming: boolean = false;

	/** Handle for the inactivity timeout, null when no timeout is active */
	private timeout: number | null = null;
	private timeoutWarning: number | null = null;
	private timeoutStartTime: number | null = null;
	private timeoutId: string | null = null;

	/** Flag indicating whether finish has been called for this stream */
	protected isStreamFinished: boolean = false;

	private finalAdf: DocNode | undefined;
	private actionPolicy: ActionPolicy | undefined;
	private pendingActions = new Set<{ aborted: boolean }>();
	private strictAction: StrictAction | undefined;
	private executingStrictAction: StrictAction | undefined;

	/** Allow the concrete update route to reset its processor for a new action. */
	protected startAction(_strictFifo: boolean, _onExecutionTimeout?: () => void): void {}

	private getStreamId(payload: TCommandProperties): string {
		return JSON.stringify([payload.senderId, payload.groupId, payload.key]);
	}

	private updateInactivityTimeout(payload: TCommandProperties): void {
		this.startInactivityTimeout(typeof payload.traceId === 'string' ? payload.traceId : undefined);
		if (payload.isStreamComplete) {
			this.clearInactivityTimeout();
		}
	}

	private executeStrictAction(payload: TCommandProperties, action: StrictAction): Promise<void> {
		if (action.aborted) {
			return Promise.resolve();
		}
		if (payload.isStreamComplete) {
			action.closed = true;
		}
		action.work = action.work.then(async () => {
			if (action.aborted) {
				return;
			}
			try {
				if (!action.started) {
					action.started = true;
					this.executingStrictAction = action;
					this.startAction(true, () => {
						if (
							this.executingStrictAction === action &&
							!action.aborted &&
							!this.isStreamFinished
						) {
							this.handleAbort('timeout');
							this.props.onStreamTimeout?.({
								streamType: this.streamType,
								traceId: typeof payload.traceId === 'string' ? payload.traceId : undefined,
							});
						}
					});
					this.isStreaming = true;
					this.isStreamFinished = false;
					this.finalAdf = undefined;
					this.props.onStreamStart?.({ streamType: this.streamType, payload });
				}
				await this.process(payload);
				if (action.aborted) {
					return;
				}
				this.props.onStream?.({ streamType: this.streamType, payload });
				if (payload.isStreamComplete) {
					await this.completeStrictAction(action);
					if (action.aborted) {
						return;
					}
					this.props.onStreamEnd?.({ streamType: this.streamType, payload, adfDoc: this.finalAdf });
				}
			} catch (error) {
				if (!action.aborted) {
					this.props.onStreamError?.({
						error,
						streamType: this.streamType,
						traceId: typeof payload.traceId === 'string' ? payload.traceId : undefined,
					});
					this.handleAbort('error', { errorMessage: ErrorUtils.extractErrorMessage(error) });
				}
			}
		});
		return action.work;
	}

	private completeStrictAction(action: StrictAction): Promise<void> {
		action.completion ??= (async () => {
			if (action.aborted) {
				return;
			}
			const adfDoc = await this.finishAction();
			if (action.aborted) {
				return;
			}
			this.finalAdf = adfDoc;
			if (adfDoc && this.props.reuploadImages) {
				await this.props.reuploadImages();
			}
			if (!action.aborted) {
				this.isStreamFinished = true;
				this.isStreaming = false;
				this.pendingActions.delete(action);
				if (this.executingStrictAction === action) {
					this.executingStrictAction = undefined;
				}
			}
		})();
		return action.completion;
	}

	/**
	 * Creates a new streaming command handler instance.
	 *
	 * @param props - Pass-through props containing callbacks and utilities
	 * @param streamType - The type of stream this handler manages (create, update, etc.)
	 */
	constructor(
		props: PassThroughCommandProps,
		protected streamType: StreamType,
	) {
		super(props);
	}

	/**
	 * Clears any existing inactivity timeout.
	 * This stops the timeout from firing if it hasn't already,
	 * preventing unwanted aborts of active streams.
	 */
	clearInactivityTimeout(): void {
		if (this.timeoutWarning !== null) {
			clearTimeout(this.timeoutWarning);
			this.timeoutWarning = null;
		}
		if (this.timeoutStartTime !== null) {
			const delayTime = performance.now() - this.timeoutStartTime;
			if (delayTime > DELAYED_REPORTING_THRESHOLD_MS) {
				this.props.editorAPI.analytics?.actions.fireAnalyticsEvent<
					TimeoutWarningPayload,
					'customEventType'
				>(
					createWarningStreamPayload({
						delayedAtMs: delayTime,
						success: 'delayed',
						id: this.timeoutId,
					}),
				);
			}
		}
		this.timeoutStartTime = null;
		this.timeoutId = null;
		if (this.timeout !== null) {
			clearTimeout(this.timeout);
			this.timeout = null;
		}
	}

	/**
	 * Starts or restarts the inactivity timeout for the current stream.
	 * If no stream activity occurs within the timeout period (30 seconds),
	 * the stream will be automatically aborted and cleaned up.
	 *
	 * This helps prevent hanging streams and resource leaks when
	 * the Rovo bridge stops sending data unexpectedly.
	 */
	startInactivityTimeout(traceId: string | undefined): void {
		this.clearInactivityTimeout();

		this.timeoutStartTime = performance.now();
		this.timeoutId = crypto.randomUUID();
		this.timeout = setTimeout(() => {
			// abort when timeout occurs
			this.handleAbort('timeout');
			this.props.onStreamTimeout?.({ streamType: this.streamType, traceId });
		}, ABORT_TIMEOUT_MS) as unknown as number;

		// Set a warning timeout for instrumentation
		this.timeoutWarning = setTimeout(() => {
			// Fire warning
			this.props.editorAPI.analytics?.actions.fireAnalyticsEvent<
				TimeoutWarningPayload,
				'customEventType'
			>(
				createWarningStreamPayload({
					delayedAtMs: WARNING_TIMEOUT_MS,
					success: 'warning',
					id: this.timeoutId,
				}),
			);
		}, WARNING_TIMEOUT_MS) as unknown as number;
	}

	/**
	 * Main entry point for processing streaming commands.
	 * Manages the complete lifecycle of a streaming operation including:
	 * - Timeout management and state tracking
	 * - Stream start notifications for first-time streams
	 * - Processing individual stream chunks
	 * - Stream completion and cleanup
	 * - Error handling and recovery
	 *
	 * @param payload - Command payload containing stream data and completion status
	 * @throws Will catch and forward any errors to the error callback
	 */
	async execute(payload: TCommandProperties): Promise<void> {
		if (this.streamType === 'update') {
			const id = this.getStreamId(payload);
			const previousPolicy = this.actionPolicy;
			let policy: ActionPolicy;
			if (!previousPolicy || previousPolicy.id !== id || previousPolicy.closed) {
				const previous = this.strictAction;
				if (previous && !previous.aborted && !previous.closed) {
					this.handleAbort('aborted');
				}
				const strictFifo = payload.key === 'aifc-adf-update' && isStrictFifoToolCallsEnabled();
				policy = { id, strictFifo, closed: false, aborted: false };
				this.actionPolicy = policy;
				this.strictAction = strictFifo
					? {
							closed: false,
							aborted: false,
							started: false,
							work:
								previousPolicy && !previousPolicy.aborted
									? (previousPolicy.work ?? Promise.resolve())
									: Promise.resolve(),
						}
					: undefined;
				if (this.strictAction) {
					this.pendingActions.add(this.strictAction);
				} else if (payload.key === 'aifc-adf-update' || previousPolicy?.strictFifo) {
					const previousWork =
						previousPolicy && !previousPolicy.aborted && (previous || previousPolicy.ready)
							? previousPolicy.work
							: undefined;
					if (previousWork) {
						this.pendingActions.add(policy);
						policy.ready = previousWork.then(() => {
							if (!policy.aborted) {
								this.startAction(false);
							}
						});
					} else {
						this.startAction(false);
					}
				}
			} else {
				policy = previousPolicy;
			}
			// ACTION_COMPLETE is the input boundary, not application completion.
			// Retain an aborted policy until its boundary so remaining chunks stay discarded.
			if (payload.isStreamComplete) {
				policy.closed = true;
			}
			if (policy.aborted) {
				return;
			}
			// Time incoming data at admission; deferred application must not reset
			// or clear the timer belonging to a later incoming action.
			this.updateInactivityTimeout(payload);
			if (policy.strictFifo && this.strictAction) {
				const work = this.executeStrictAction(payload, this.strictAction);
				policy.work = work;
				return work;
			}
			if (policy.ready) {
				const work = (policy.work ?? policy.ready).then(async () => {
					if (!policy.aborted) {
						await this.executeLegacy(payload, policy);
					}
					if (payload.isStreamComplete) {
						this.pendingActions.delete(policy);
						policy.ready = undefined;
					}
				});
				policy.work = work;
				return work;
			}
			const work = this.executeLegacy(payload, policy);
			policy.work = work;
			return work;
		}
		await this.executeLegacy(payload);
	}

	private async executeLegacy(payload: TCommandProperties, policy?: ActionPolicy): Promise<void> {
		try {
			// Reset timeout on each chunk to prevent premature timeouts
			if (!policy) {
				this.startInactivityTimeout(
					typeof payload.traceId === 'string' ? payload.traceId : undefined,
				);
			}

			// Initialize streaming state and notify listeners on first chunk
			if (!this.isStreaming) {
				this.isStreaming = true;
				this.isStreamFinished = false;
				this.finalAdf = undefined;
				this.props.onStreamStart?.({ streamType: this.streamType, payload });
			}

			// Process the current stream chunk
			await this.process(payload);
			if (policy?.aborted) {
				return;
			}
			this.props.onStream?.({ streamType: this.streamType, payload });

			// Handle stream completion - finalize processing and run cleanup
			if (payload.isStreamComplete) {
				if (!policy) {
					this.clearInactivityTimeout();
				}
				// Queued updates finish their captured scope; other legacy handlers keep
				// their overridable finish entry point.
				if (policy?.ready || this.strictAction) {
					await this.finishLegacy(policy);
				} else {
					await this.finish();
				}
				if (policy?.aborted) {
					return;
				}
				this.isStreaming = false;
				this.props.onStreamEnd?.({ streamType: this.streamType, payload, adfDoc: this.finalAdf });
			}
		} catch (error) {
			if (policy?.aborted) {
				return;
			}
			this.props.onStreamError?.({
				error,
				streamType: this.streamType,
				traceId: typeof payload.traceId === 'string' ? payload.traceId : undefined,
			});
			this.handleAbort('error', { errorMessage: ErrorUtils.extractErrorMessage(error) });
		}
	}

	/**
	 * Abstract method for processing individual stream chunks.
	 * Concrete implementations should handle the specific logic for their stream type.
	 *
	 * This method is called for each chunk of streaming data received,
	 * allowing incremental processing and real-time updates.
	 *
	 * @param payload - Command properties containing the current stream chunk data
	 */
	abstract process(payload: TCommandProperties): Promise<void> | void;

	/**
	 * Finalizes the streaming operation.
	 * Uses a flag to ensure finishAction is only called once during the stream lifecycle.
	 * Automatically triggers image re-uploading for supported stream types.
	 */
	async finish(): Promise<void> {
		if (this.actionPolicy?.aborted) {
			return;
		}
		const action = this.strictAction;
		if (action) {
			if (!action.closed) {
				action.closed = true;
				if (this.actionPolicy) {
					this.actionPolicy.closed = true;
				}
				this.clearInactivityTimeout();
				action.work = action.work.then(() => this.completeStrictAction(action));
			}
			if (this.actionPolicy) {
				this.actionPolicy.work = action.work;
			}
			return action.work;
		}
		const policy = this.actionPolicy;
		if (policy?.ready) {
			policy.closed = true;
			this.clearInactivityTimeout();
			const work = (policy.work ?? policy.ready).then(async () => {
				if (!policy.aborted) {
					await this.finishLegacy(policy);
					if (!policy.aborted) {
						this.isStreaming = false;
					}
				}
				this.pendingActions.delete(policy);
				policy.ready = undefined;
			});
			policy.work = work;
			return work;
		}
		await this.finishLegacy();
	}

	private async finishLegacy(policy?: ActionPolicy): Promise<void> {
		if (this.isStreamFinished) {
			return;
		}

		this.isStreamFinished = true;
		const adfDoc = await this.finishAction();
		if (policy?.aborted) {
			return;
		}
		this.finalAdf = adfDoc;

		// Trigger image re-uploading if we have a document and handler
		if (this.finalAdf && this.props.reuploadImages) {
			await this.props.reuploadImages();
		}
	}

	/**
	 * Abstract method called when the stream is complete.
	 * Implementations should finalize processing and return the processed ADF document if one was produced.
	 *
	 * @returns The processed ADF document, or undefined if none was produced
	 */
	abstract finishAction(): Promise<DocNode | undefined> | undefined;

	handleAbort(reason?: AbortReason, options?: AbortOptions): void {
		log(`handle abort ${this.streamType} streaming command handler`);
		for (const action of this.pendingActions) {
			action.aborted = true;
		}
		this.pendingActions.clear();
		if (this.strictAction) {
			this.strictAction.aborted = true;
		}
		if (this.actionPolicy && (this.actionPolicy.strictFifo || this.actionPolicy.ready)) {
			this.actionPolicy.aborted = true;
		}
		this.isStreaming = false;
		this.isStreamFinished = false;
		this.finalAdf = undefined;
		this.clearInactivityTimeout();
		this.abort(reason, options);
	}

	/**
	 * Abstract method for aborting the streaming operation.
	 * Implementations should immediately stop processing and clean up resources.
	 *
	 * `reason` and `options` are carried through to session-outcome telemetry
	 * so a user cancel can be told apart from a timeout or an error.
	 */
	abstract abort(reason?: AbortReason, options?: AbortOptions): void;

	deregister(): void {
		log(`deregister ${this.streamType} streaming command handler`);
		if (this.pendingActions.size > 0) {
			this.handleAbort('aborted');
		}
		this.isStreaming = false;
		this.isStreamFinished = false;
		this.finalAdf = undefined;
		this.clearInactivityTimeout();
	}
}
